import qs from 'qs';
import type { LinkProps, Location } from 'react-router';
import { parsePath } from 'react-router';

import type { NavAction } from './NavAction';

interface CombinedNavActionResult extends Pick<
	Location,
	'pathname' | 'search' | 'hash' | 'state'
> {
	replace: boolean;
}

export function resolveNavAction(
	location: Omit<Location, 'key'>,
	...navActions: NavAction[]
) {
	// eslint-disable-next-line unicorn/no-array-reduce
	const finalResult = navActions.reduce<CombinedNavActionResult>(
		(args, navAction) => {
			let result =
				typeof navAction === 'function'
					? navAction({
							pathname: args.pathname,
							search: args.search,
							hash: args.hash,
							state: args.state,

							// Use a lazy memoized getter so that parsing is skipped when it is
							// not used.
							//
							// We must make a new object with this property. It cannot be added to
							// the location object itself because location is a frozen (read-only)
							// object in development mode.
							get parsedSearch() {
								// @ts-expect-error Getter must be removed before assigning a value.
								delete this.parsedSearch;
								this.parsedSearch = qs.parse(args.search, {
									ignoreQueryPrefix: true,
								});
								return this.parsedSearch;
							},
						})
					: navAction;

			// `undefined` means the NavAction does nothing.
			if (result === undefined) {
				return args;
			}

			if (typeof result === 'string') {
				result = parsePath(result);
			}

			return {
				pathname: result.pathname || args.pathname,
				search:
					typeof result.search === 'string'
						? result.search
						: qs.stringify(result.search, { addQueryPrefix: true }),
				hash: result.hash || '',
				state: result.state ?? null,
				// If any NavAction is a push, then the entire result is a push.
				replace: (result.replace ?? false) && args.replace,
			};
		},
		{ ...location, replace: true }
	);

	const { state, replace, ...to } = finalResult;
	return { to, state, replace } satisfies Pick<
		LinkProps,
		'to' | 'state' | 'replace'
	>;
}
